import { parseDateOnly } from '@/lib/utils/date-only'
import type { VendorPORow } from '../api/types'

/**
 * Revised Ship Date status: the tracker stage, except an awaiting line
 * with a logged estimate reads `estimate_logged` (computed server-side,
 * `ship_status` in vendor_pos.py). Local to procurement: the cross-module
 * import ban keeps this from sharing po-ship-confirmation's stage meta.
 */
export type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

const LABELS: Record<string, string> = {
  awaiting_confirmation: 'Awaiting Confirmation',
  estimate_logged: 'Estimated Ship Date Logged',
  confirmed: 'Confirmed',
  received: 'Received',
  cancelled: 'Cancelled',
}

export function shipStatusLabel(status: string | null | undefined): string {
  if (!status) return ''
  return (
    LABELS[status] ??
    status
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  )
}

export function shipStatusVariant(status: string | null | undefined): ChipVariant {
  switch (status) {
    case 'confirmed':
      return 'success'
    case 'estimate_logged':
      return 'info'
    case 'awaiting_confirmation':
      return 'warning'
    case 'cancelled':
      return 'danger'
    default:
      return 'neutral'
  }
}

function shortDate(iso: string): string {
  try {
    return parseDateOnly(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

function slipText(days: number): string {
  if (days === 0) return 'no slip'
  const n = Math.abs(days)
  return days > 0 ? `slipped ${n} day${n === 1 ? '' : 's'}` : `pulled in ${n} day${n === 1 ? '' : 's'}`
}

/** Hover text for the cell, e.g. "Estimate logged 3× · first Sep 21 ·
 *  slipped 16 days · last by cnorris on Oct 10". */
export function shipStatusTooltip(row: VendorPORow): string | undefined {
  const parts: string[] = []
  const count = row.estimate_count ?? 0
  if (count > 0) {
    parts.push(`Estimate logged ${count}×`)
    if (row.first_logged_estimate) parts.push(`first ${shortDate(row.first_logged_estimate)}`)
    if (row.estimate_slip_days != null && count > 1) parts.push(slipText(row.estimate_slip_days))
    if (row.last_estimate_logged_at) {
      const who = row.last_estimate_logged_by?.split('@')[0]
      const when = new Date(row.last_estimate_logged_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
      parts.push(who ? `last by ${who} on ${when}` : `last on ${when}`)
    }
  } else if ((row.ship_status ?? row.ship_confirmation_stage) === 'awaiting_confirmation') {
    parts.push('No estimate logged yet (date from the Odoo PO)')
  }
  if (row.ship_confirmation_source) {
    parts.push(`Source: ${shipStatusLabel(row.ship_confirmation_source)}`)
  }
  return parts.length ? parts.join(' · ') : undefined
}
